import produce from 'immer';
import { create } from 'zustand';
import { getFeatureFlag } from '~/services/producer/FeatureFlags/FeatureFlags';

export type FeatureFlag = {
  name: string;
  isEnabled: boolean;
};

export type FeatureFlagState = {
  flags: FeatureFlag[];
  isLoading: boolean;
  isError: boolean;
  isSuccessful: boolean;
};

export type FeatureFlagActions = {
  fetchFeatureFlags: () => Promise<void>;
  refreshSingleFeatureFlag: (flagName: FeatureFlags) => Promise<void>;
  refreshAllFlags: (email: string) => Promise<void>;
  setState: (newState: Partial<FeatureFlagState>) => void;
  isRealPhotosEnabledForEmail: (email: string) => boolean;
  isFeatureFlagEnabled: (flagName: FeatureFlags) => boolean;
  isEmailFeatureFlagEnabled: (email: string, flagName: EmailFeatureFlags) => boolean;
};

export type FeatureFlagStore = FeatureFlagState & FeatureFlagActions;

const initialFeatureFlagState: FeatureFlagState = {
  flags: [],
  isLoading: false,
  isError: false,
  isSuccessful: false,
};

export enum FeatureFlags {
  PriceVariation = 'PriceVariation',
  VerifyOffers = 'VerifyOffers',
  PriceVariationDecreasePrice = 'PriceVariationDecreasePrice',
  RealPhotos = 'RealPhotos',
  ClassifyPlants = 'ClassifyPlants',
}

export enum EmailFeatureFlags {
  RealPhotos = 'RealPhotos:',
}

const predefinedFlags = Object.values(FeatureFlags);
const predefinedEmailFlags = Object.values(EmailFeatureFlags);

const createEmailFlag = (email: string, flag: EmailFeatureFlags): string => `${flag}${email}`;

const createLoadingState = (isLoading: boolean) => ({
  isLoading,
  isError: false,
  isSuccessful: !isLoading,
});

const createSuccessState = () => ({
  isLoading: false,
  isError: false,
  isSuccessful: true,
});

const createErrorState = () => ({
  isLoading: false,
  isError: true,
  isSuccessful: false,
});

export const useFeatureFlagStore = create<FeatureFlagStore>()((set, get) => ({
  ...initialFeatureFlagState,

  setState: (newState: Partial<FeatureFlagState>) => {
    set(
      produce((state: FeatureFlagStore) => {
        Object.assign(state, newState);
      }),
    );
  },

  fetchFeatureFlags: async () => {
    const { setState } = get();

    setState({ isLoading: true });

    try {
      const flags = await Promise.all(predefinedFlags.map(flag => getFeatureFlag({ flag })));

      setState({
        flags,
        isError: false,
        isLoading: false,
        isSuccessful: true,
      });
    } catch (error) {
      setState({
        flags: [],
        isError: true,
        isLoading: false,
        isSuccessful: false,
      });
    }
  },

  refreshSingleFeatureFlag: async (flagName: FeatureFlags) => {
    const { setState, flags } = get();

    try {
      const updatedFlag = await getFeatureFlag({ flag: flagName });
      const updatedFlags = flags.map(flag => (flag.name === flagName ? updatedFlag : flag));

      setState({
        flags: updatedFlags,
        isError: false,
        isLoading: false,
        isSuccessful: true,
      });
    } catch (error) {
      setState({
        isError: true,
        isLoading: false,
        isSuccessful: false,
      });
    }
  },

  refreshAllFlags: async (email: string) => {
    if (!email) return;

    const { setState } = get();
    setState(createLoadingState(true));

    try {
      const flags = predefinedFlags.map(flag => getFeatureFlag({ flag }));
      const emailFlags = predefinedEmailFlags.map(flag =>
        getFeatureFlag({ flag: createEmailFlag(email, flag) }),
      );

      const allFlagsResult = await Promise.all([...flags, ...emailFlags]);

      setState({
        flags: allFlagsResult,
        ...createSuccessState(),
      });
    } catch (error) {
      setState(createErrorState());
    }
  },

  isRealPhotosEnabledForEmail: (email: string): boolean => {
    if (!email) return false;

    try {
      const emailFlagName = createEmailFlag(email, EmailFeatureFlags.RealPhotos);
      const emailFlag = get().flags.find(flag => flag.name === emailFlagName);
      return emailFlag?.isEnabled ?? false;
    } catch {
      return false;
    }
  },

  isFeatureFlagEnabled: (flagName): boolean => {
    const { flags } = get();
    const flag = flags?.find(it => it.name === flagName);
    return flag?.isEnabled ?? false;
  },
  isEmailFeatureFlagEnabled: (email: string, flagName: EmailFeatureFlags): boolean => {
    const { flags } = get();
    const flagWithEmail = createEmailFlag(email, flagName);
    const flag = flags?.find(it => it.name === flagWithEmail);
    return flag?.isEnabled ?? false;
  },
}));
